/**
 * Lindy Product Details - SKU Update Listener
 * Listens for theme's VariantUpdateEvent and updates SKU
 */

import { ThemeEvents } from '@theme/events';

class ProductDetailsLindy extends HTMLElement {
  connectedCallback() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    section.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);

    // Initialize price skeleton handlers
    this.initPriceSkeletons();
  }

  disconnectedCallback() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    section.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  handleVariantUpdate = (event) => {
    // Check if this event is for our product
    if (event.target instanceof HTMLElement &&
        event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { html } = event.detail.data;

    // Find the updated product details block in the fetched HTML
    const newBlock = html.querySelector('product-details-lindy-component');
    if (!newBlock) return;

    // Update SKU/Part Number
    const currentSku = this.querySelector('.product-details-lindy__part-number');
    const newSku = newBlock.querySelector('.product-details-lindy__part-number');

    if (currentSku && newSku) {
      currentSku.textContent = newSku.textContent;
    } else if (currentSku && !newSku) {
      // New variant has no SKU, hide it
      currentSku.style.display = 'none';
    } else if (!currentSku && newSku) {
      // New variant has SKU but current doesn't, add it
      const titleSection = this.querySelector('.product-details-lindy__title-section');
      if (titleSection) {
        const skuClone = newSku.cloneNode(true);
        titleSection.appendChild(skuClone);
      }
    }
  };

  /**
   * Initialize price skeleton loaders
   * Watch for BSS B2B to update prices and hide skeleton when done
   */
  initPriceSkeletons() {
    const priceContainers = this.querySelectorAll('[data-price-container]');

    priceContainers.forEach((container) => {
      const skeleton = container.querySelector('.product-details-lindy__price-skeleton');
      const priceContainer = container.querySelector('[ref="priceContainer"]');

      if (!skeleton || !priceContainer) return;

      let hasShownPrice = false;

      // Function to show price and hide skeleton
      const showPriceAndHideSkeleton = () => {
        if (hasShownPrice) return;
        hasShownPrice = true;

        // Show price with fade-in
        priceContainer.style.opacity = '1';

        // Wait for transition end before hiding skeleton
        const hideSkeleton = () => {
          skeleton.style.display = 'none';
          priceContainer.removeEventListener('transitionend', hideSkeleton);
        };

        priceContainer.addEventListener('transitionend', hideSkeleton, { once: true });
      };

      // Check if price is ready to be shown
      const isPriceReady = () => {
        // BSS B2B has injected prices
        const hasBSSPrice = priceContainer.hasAttribute('bss-b2b-product-active') ||
                           priceContainer.querySelector('.bss-b2b-price-no-vat') ||
                           priceContainer.querySelector('.bss-b2b-price-has-vat');

        if (!hasBSSPrice) return false;

        // Check if GST price display has already processed the prices
        // (one of the price spans should be visible)
        const noVatPrice = priceContainer.querySelector('.bss-b2b-price-no-vat');
        const hasVatPrice = priceContainer.querySelector('.bss-b2b-price-has-vat');

        // If we have BSS prices, check if at least one is visible (not display: none)
        if (noVatPrice || hasVatPrice) {
          const noVatVisible = noVatPrice && noVatPrice.style.display !== 'none';
          const hasVatVisible = hasVatPrice && hasVatPrice.style.display !== 'none';

          // At least one price should be visible
          return noVatVisible || hasVatVisible;
        }

        return true; // If no GST-specific prices, BSS price is enough
      };

      // Watch for BSS B2B and GST price display updates
      const observer = new MutationObserver(() => {
        if (isPriceReady()) {
          showPriceAndHideSkeleton();
          observer.disconnect();
        }
      });

      // Observe the price container for changes
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['bss-b2b-product-active', 'style']
      });

      // Also listen to gst-change event to immediately show price when GST preference changes
      const handleGSTChange = () => {
        if (isPriceReady()) {
          showPriceAndHideSkeleton();
        }
      };

      window.addEventListener('gst-change', handleGSTChange);

      // Cleanup
      const cleanup = () => {
        window.removeEventListener('gst-change', handleGSTChange);
        observer.disconnect();
      };

      // Fallback: Force show price after 3 seconds if BSS B2B hasn't updated
      setTimeout(() => {
        if (!hasShownPrice) {
          showPriceAndHideSkeleton();
          cleanup();
        }
      }, 3000);
    });
  }
}

if (!customElements.get('product-details-lindy-component')) {
  customElements.define('product-details-lindy-component', ProductDetailsLindy);
}
